import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import {
  CoreDataEntity,
  DataService,
  IPersistable,
  PersistenceService,
  PersistMode,
  QueryColumn,
} from '@barthauer/abc/core/data';
import {
  AbcButtonComponent,
  AbcContainerService,
  AbcGridComponent,
  AbcLoaderComponent,
  AbcWindowComponent,
  ColumnHeader,
  DataType,
  GridStyle,
  IGrid,
  ILoader,
  IWindow,
  WindowStyle,
} from '@barthauer/abc/ui-components';
import { Observable, Subscription } from 'rxjs';
import { DocumentsEditorsService } from '../../services/documents-editors.service';
import { DocumentsEditorTableDetailsComponent } from './document-editor-table-details/documents-editor-table-details.component';

@Component({
    selector: 'documents-editor',
    templateUrl: './documents-editor-table.component.html',
    styleUrls: ['./documents-editor-table.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class DocumentsEditorTableComponent implements OnInit, AfterViewInit {
  private gridInstance: AbcGridComponent;
  private _parentInfo: CoreDataEntity;
  private parentId: string;
  private objectType: string;
  private documentQuery;
  private abcLoader;
  public componentHTML: string;
  documentData;
  private subscriptions: Subscription[] = [];
  windowInstance: AbcWindowComponent;

  @Input()
  get parentInfo(): CoreDataEntity {
    return this._parentInfo;
  }
  set parentInfo(parentInfo: CoreDataEntity) {
    this.parentId = parentInfo.id;
    this.objectType = parentInfo.objectType.toString();
    this.subscriptions.push(
      this.prepareRequestDocuments(parentInfo).subscribe((data) => {
        this.gridInstance.gridData = data;
        this.gridInstance.updateData(data);
      })
    );
  }
  @ViewChild('documentGridContainer', { read: ViewContainerRef }) documentGrid: AbcGridComponent;
  @ViewChild('abcWindowContainer', { read: ViewContainerRef }) abcWindow: ViewContainerRef;
  @ViewChild('addRowButton', { static: true }) addRowButton: AbcButtonComponent;
  @ViewChild('deleteRowButton', { static: true }) deleteRowButton: AbcButtonComponent;
  @ViewChild('editModeButton', { static: true }) editModeButton: AbcButtonComponent;
  @ViewChild('hideGroupingButton', { static: true }) hideGroupingButton: AbcButtonComponent;
  @ViewChild('documentLoaderContainer', { static: true, read: ViewContainerRef })
  documentLoaderContainerRef: ViewContainerRef;

  columnsHeader: ColumnHeader[] = [
    { name: 'Designation', dataField: 'Designation', type: DataType.String, editable: true },
    {
      name: 'Kind',
      dataField: 'Kind',
      type: DataType.DropDownList,
      editable: true,
      dropDownSource: ['DOC', 'FILE', 'FSB', 'GEN', 'IMAGE', 'LINK', 'PATH', 'VIDEO', 'WRB'],
    },
    { name: 'Filename / link', dataField: 'FileName', type: DataType.String, editable: true },
    { name: 'Date', dataField: 'Date', type: DataType.DateTimeInput, editable: true },
    {
      name: '',
      dataField: 'details',
      editable: false,
      type: DataType.Button,
      actionSvgIconName: 'assets/images/detailsWebArena_32x32.png',
      actionMethod: (row) => {
        this.openDetailsWindow(row);
      },
    },
    {
      name: '',
      dataField: 'download',
      editable: false,
      type: DataType.Button,
      actionSvgIconName: 'assets/images/OpenDocument_32x32.png',
      actionMethod: (row) => this.downloadFile(row),
    },
    { name: 'Id', dataField: 'Id', type: DataType.String, hidden: true },
  ];

  constructor(
    private coreDataService: DataService,
    private abcContainer: AbcContainerService,
    private hostElement: ElementRef,
    private persistenceService: PersistenceService,
    private documentsEditorsService: DocumentsEditorsService
  ) {
    this.componentHTML = this.hostElement.nativeElement.outerHTML;
  }

  ngOnInit() {
    this.abcLoader = this.createAbcLoader();
    this.addRowButton.addEventListener('click', () => {
      this.gridInstance.addEmptyRow('last');
    });

    this.deleteRowButton.addEventListener('click', () => {
      let rowIndex: number = this.gridInstance.getselectedrowindex();
      let rowData = this.gridInstance.getRowData(rowIndex);
      const objectKey: IPersistable = {
        id: rowData.Id,
        objectType: 'Document',
        persistenceCategory: null,
        changesPersisted: (persistMode) => (persistMode = PersistMode.Delete),
        revertChanges: () => {},
      };
      this.persistenceService.addObjectForDelete(objectKey);
      this.persistenceService.persistPendingChanges();
      this.gridInstance.deleteSelectedRow();
    });

    this.hideGroupingButton.addEventListener('click', () => {
      if (this.gridInstance.groupable) {
        this.gridInstance.setGridOptions('groupable', false);
        this.gridInstance.groupable = false;
      } else {
        this.gridInstance.setGridOptions('groupable', true);
        this.gridInstance.groupable = true;
      }
    });

    this.documentsEditorsService.updateData$.subscribe(() => {
      this.abcLoader.showLoader();
      this.updateGridData();
    });
    this.documentsEditorsService.closeDialog$.subscribe(() => {
      this.windowInstance.closeDialog();
    });
  }

  ngAfterViewInit() {
    this.generateGrid();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((element) => {
      element.unsubscribe();
    });
  }

  createAbcLoader() {
    const LoaderAttr: ILoader = {
      id: 'loader-dataForm',
      value: 0,
      color: 'primary',
      mode: 'indeterminate',
      percentageValue: false,
      text: 'Waiting...',
    };
    return this.abcContainer.createComponent(this.documentLoaderContainerRef, AbcLoaderComponent, LoaderAttr)
      .componentRef.instance;
  }

  private generateGrid() {
    const style: GridStyle = {
      sortable: true,
      pageable: true,
      editable: true,
      filterable: true,
      showfilterrow: true,
      groupable: false,
      autorowheight: false,
      showemptyrow: false,
      showBorders: false,
      showColumnLines: false,
      showRowLines: false,
      visible: true,
      rowsheight: 30,
      width: '100%',
      autoshowcolumnsmenubutton: true,
      autoshowfiltericon: true,
      enableellipsis: true,
    };
    const GridAttr: IGrid = {
      id: 'grid-DocumentsEditor',
      columnsHeader: this.columnsHeader,
      gridData: {},
      setGridStyle: (GridStyle) => (GridStyle = style),
    };

    const grid = this.abcContainer.createComponent(this.documentGrid, AbcGridComponent, GridAttr);
    this.gridInstance = <AbcGridComponent>grid.componentRef.instance;
    this.gridInstance.style = 4;

    const objectKey: IPersistable = {
      id: null,
      objectType: 'Document',
      persistenceCategory: null,
      changesPersisted: (persistMode) => (persistMode = PersistMode.Insert),
      revertChanges: () => {},
    };

    this.gridInstance.onRowInserted.subscribe(
      (event) => {
        let rowData = event.event.data;
        if (rowData) {
          this.persistenceService.addObjectForInsert(objectKey);
          this.addObjectValue(rowData, objectKey);
        }
      },
      (error) => {
        console.log(error);
      }
    );

    this.gridInstance.onRowUpdated.subscribe(
      (event) => {
        let rowData = event.event.data;
        if (rowData) {
          objectKey.id = rowData.Id;
          this.addObjectValue(rowData, objectKey);
        }
      },
      (error) => {
        console.log(error);
      }
    );
  }

  private addObjectValue(rowData, objectKey: IPersistable) {
    for (const property in rowData) {
      this.persistenceService.addObjectValue(objectKey, property, rowData[property]);
    }
    this.persistenceService.addObjectValue(objectKey, 'ParentId', this.parentId);
    this.persistenceService.addObjectValue(objectKey, 'ParentObjectType', this.objectType);
    this.persistenceService.persistPendingChanges();
  }

  private prepareRequestDocuments(parentInfo: CoreDataEntity): Observable<any> {
    const queryColumn = new Array<QueryColumn>();
    queryColumn.push(
      {
        Name: 'Comment',
        OPath: 'Comment',
      },
      {
        Name: 'Date',
        OPath: 'Date',
      },
      {
        Name: 'Designation',
        OPath: 'Designation',
      },
      {
        Name: 'FileName',
        OPath: 'FileName',
      },
      {
        Name: 'Id',
        OPath: 'Id',
      },
      {
        Name: 'InsertDate',
        OPath: 'InsertDate',
      },
      {
        Name: 'Kind',
        OPath: 'Kind',
      },
      {
        Name: 'Number',
        OPath: 'Number',
      },
      {
        Name: 'ParentId',
        OPath: 'ParentId',
      },
      {
        Name: 'Object',
        OPath: 'Object',
      },
      {
        Name: 'ProgramPath',
        OPath: 'ProgramPath',
      },
      {
        Name: 'UpdateDate',
        OPath: 'UpdateDate',
      },
      {
        Name: 'DataCarrier',
        OPath: 'Media',
      },
      {
        Name: 'ValidFrom',
        OPath: 'DateStart',
      },
      {
        Name: 'ValidUntil',
        OPath: 'DateEnd',
      }
    );

    const observable = new Observable((observer) => {
      this.documentQuery = this.coreDataService.createSimpleQuery(
        '',
        'Document',
        `ParentId='${parentInfo.id}'`,
        queryColumn
      );
      this.coreDataService.executeReadObjectsQuery(this.documentQuery).subscribe((data) => {
        this.documentData = data;
        observer.next(data);
      });
    });
    return observable;
  }

  updateGridData() {
    this.coreDataService.executeReadObjectsQuery(this.documentQuery).subscribe((data) => {
      this.gridInstance.updateData(data);
      this.documentData = data;
      this.abcLoader.hideLoader();
    });
  }

  openDetailsWindow(rowData) {
    const style: WindowStyle = {
      width: '500px',
      height: '500px',
      minWidth: '500px',
    };

    const WindowAttr: IWindow = {
      id: 'documents-Editor-Details',
      label: 'Document',
      minimizeButton: false,
      maximizeButton: false,
      component: DocumentsEditorTableDetailsComponent,
      setWindowStyle: (windowStyle) => (windowStyle = style),
    };
    const window = this.abcContainer.createComponent(this.abcWindow, AbcWindowComponent, WindowAttr);
    this.windowInstance = <AbcWindowComponent>window.componentRef.instance;
    this.windowInstance.createComponentInstance().subscribe((activeComponent) => {
      const componentInstance = activeComponent.openWindow();
      const componentRef = <DocumentsEditorTableDetailsComponent>componentInstance.instance;
      activeComponent.onAfterOpened.subscribe(() => {
        componentRef.parentInfo = { documentData: rowData, objectType: this.objectType };
      });
    });
  }

  downloadFile(rowData) {
    let fileNamePath: string = rowData.FileName;
    let fileName: string;

    const a = document.createElement('a');
    const fileReader = new FileReader();
    if (fileNamePath) {
      this.documentsEditorsService.readFile(fileNamePath).subscribe(
        (res) => {
          fileReader.readAsDataURL(res);
          let href;
          fileReader.onload = function () {
            href = fileReader.result;
            a.setAttribute('href', fileReader.result.toString());
            a.setAttribute('download', fileName);
            a.click();
          };
        },
        (error: HttpErrorResponse) => {
          console.log(error);
        }
      );
    } else {
      alert('no file found');
    }
  }
}
